import { Link } from "react-router-dom"
import { AlertTriangle, ArrowLeft } from "lucide-react"
import { Button } from "@edemics/ui/button"
import { Card, CardContent } from "@edemics/ui/card"

interface ModuleUnavailablePageProps {
  /**
   * Human-readable module label (e.g. "Extracurricular Clubs"). Not
   * technical — the string a tenant admin would recognise. Falls back
   * to the raw key when the caller doesn't know a nicer name.
   */
  moduleName?: string
  /** Optional list of required module keys — surfaced for support triage. */
  requiredKeys?: string[]
}

/**
 * Rendered when a tenant reaches a URL for a module that isn't enabled
 * for their tenant. Deep-links / bookmarks resolve to this instead of
 * a hard redirect so the URL stays honest and support can see exactly
 * what the user tried to reach.
 *
 * Two lanes hit this:
 *   1. <ModuleGate> when the route is wrapped and the tenant's
 *      enabledModules doesn't include the required key.
 *   2. Individual page components that call useModuleGuard() and get
 *      back a "disabled" signal.
 *
 * Kept intentionally plain — no branding tricks, no "contact your
 * admin" CTAs the tenant can't act on. A single "back to dashboard"
 * escape and an honest explanation.
 */
export function ModuleUnavailablePage({
  moduleName,
  requiredKeys,
}: ModuleUnavailablePageProps) {
  const displayName = moduleName ?? (requiredKeys ?? [])[0] ?? "This module"

  return (
    <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center p-8">
      <Card className="w-full max-w-md">
        <CardContent className="flex flex-col items-center gap-4 p-8 text-center">
          <div className="rounded-full bg-amber-100 p-3 text-amber-600">
            <AlertTriangle className="h-8 w-8" />
          </div>

          <div className="space-y-2">
            <h1 className="text-xl font-semibold text-foreground">
              {displayName} is not available
            </h1>
            <p className="text-sm text-muted-foreground">
              Your organization doesn't have access to this module yet.
              Contact your platform administrator to enable it.
            </p>
            {requiredKeys && requiredKeys.length > 0 && (
              <p className="mt-2 text-xs text-muted-foreground">
                <span className="font-medium">Module key:</span>{" "}
                <code className="rounded bg-muted px-1.5 py-0.5">
                  {requiredKeys.join(", ")}
                </code>
              </p>
            )}
          </div>

          <div className="mt-2 flex gap-2">
            <Link to="/dashboard">
              <Button variant="outline" size="sm">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Dashboard
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
